import { collector } from "./collector";
import { getEkycHeaders } from "../utils/ekycHeaders";
import { SERVER_HOST } from "../constants/config";

// Batch + flush transport. Uses the PRISTINE fetch/sendBeacon captured by the
// index.html sentinel so diagnostics traffic is neither self-instrumented by the
// network collector nor recursed. Flush triggers: interval, size/critical (via
// collector's flush handler), and page-hidden/pagehide (last-gasp).

const FLUSH_INTERVAL_MS = 10000;
const STORAGE_KEY = "ekycDiagnosticsQueue";
const KEEPALIVE_MAX_BYTES = 60 * 1024; // keepalive/beacon budget is 64 KiB

let intervalTimer = null;
let sending = false;

function rawFetch() {
  const o = window.__diagOriginals;
  return (o && o.fetch) || window.fetch;
}

function rawBeacon() {
  const o = window.__diagOriginals;
  const b = (o && o.sendBeacon) || navigator.sendBeacon;
  return b ? b.bind(navigator) : null;
}

function endpoint() {
  const vid = collector.getConfig().validationId;
  return `${SERVER_HOST}/ekyc/diagnostics/${vid}`;
}

async function flushNow() {
  if (sending || !collector.isConfigured()) return;
  const events = collector.drainBatch(100);
  if (events.length === 0) {
    replayPersisted();
    return;
  }
  sending = true;
  const payload = collector.buildPayload(events);
  try {
    const res = await rawFetch()(endpoint(), {
      method: "POST",
      headers: getEkycHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload),
    });
    if (!res || !res.ok) throw new Error("diagnostics ingest failed: " + (res && res.status));
  } catch (e) {
    // Requeue and persist for retry / next session
    collector.requeue(events);
    persist();
  } finally {
    sending = false;
  }
}

function persist() {
  try {
    const events = collector.peekAll();
    if (events.length) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(collector.buildPayload(events)));
    }
  } catch (e) {
    /* storage may be unavailable */
  }
}

function replayPersisted() {
  let raw;
  try {
    raw = sessionStorage.getItem(STORAGE_KEY);
  } catch (e) {
    return;
  }
  if (!raw) return;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    /* noop */
  }
  let payload;
  try {
    payload = JSON.parse(raw);
  } catch (e) {
    return;
  }
  if (!payload || !payload.events || !payload.events.length) return;
  if (payload.ekycValidationId !== collector.getConfig().validationId) return; // different session
  rawFetch()(endpoint(), {
    method: "POST",
    headers: getEkycHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(payload),
  }).catch(() => {});
}

// Last reliable moment (tab hidden / pagehide). Prefer keepalive fetch (can set
// the auth header); fall back to sendBeacon; if oversized, persist for replay.
function flushOnHidden() {
  if (!collector.isConfigured()) return;
  const events = collector.drainBatch(200);
  if (events.length === 0) return;
  const body = JSON.stringify(collector.buildPayload(events));
  const url = endpoint();

  if (body.length <= KEEPALIVE_MAX_BYTES) {
    try {
      rawFetch()(url, {
        method: "POST",
        headers: getEkycHeaders({ "Content-Type": "application/json" }),
        body,
        keepalive: true,
      }).catch(() => {});
      return;
    } catch (e) {
      /* fall through to beacon */
    }
  }

  const beacon = rawBeacon();
  try {
    if (beacon && body.length <= KEEPALIVE_MAX_BYTES) {
      const blob = new Blob([body], { type: "application/json" });
      if (beacon(url, blob)) return;
    }
  } catch (e) {
    /* fall through */
  }

  // Too big or beacon rejected — keep it for next-session replay.
  collector.requeue(events);
  persist();
}

function start() {
  collector.setFlushHandler((critical) => {
    if (critical) flushNow();
  });
  if (!intervalTimer) {
    intervalTimer = setInterval(flushNow, FLUSH_INTERVAL_MS);
  }
  replayPersisted();
}

function stop() {
  if (intervalTimer) {
    clearInterval(intervalTimer);
    intervalTimer = null;
  }
  collector.setFlushHandler(null);
}

export const transport = { start, stop, flushNow, flushOnHidden, persist };
